// Rift Mortar blast electricity (owner 2026-11-02). Render-only reaction to the sim's 'mortarImpact' event.
//  - 'take the yellow and the green swirls and… randomly generate electricity around the cloud of smoke. It should be
//    random, like procedurally generated' → jagged green / yellow bolts crackle over the surface of the procedurally grown
//    fireball (BlastFx.procCloud lobes), hopping between neighbouring lobes and crawling over single lobes, at random
//    places and random moments, only on the camera-facing side so they read 'around' the cloud.
//  - P1 + owner: the swirl arms 'burst outward on impact instead of winding in' → three spinning green/yellow arms that
//    unwind and fling outward from the core in the first ~0.4 s.
// Drawn with its own crisp Lightning layer (overlay: it must read on top of the opaque fire lobes).
import type * as THREE from 'three/webgpu';
import type { CloudLobe } from './blastFx';
import type { Fx } from './fx';
import type { Lightning } from './lightning';

const LIFE = 1.1, MAX = 6, ZAP_G = 0x0a9a18; // deeper green so bolts read on the pale-green cloud
interface Swirl { x: number; y: number; z: number; r: number; t: number; spin: number; zapT: number; a: number; b: number; lobes: CloudLobe[] }
const R = Math.random;

export class MortarSwirl {
  private list: Swirl[] = [];
  constructor(private bolts: Lightning, private fx: Fx) {}

  /** a Rift Mortar blast at ground height gy; lobes = the procedural cloud (spawn state); a/b = the two colours */
  add(x: number, gy: number, z: number, r: number, lobes: CloudLobe[] | null, a = 0x1fd51a, b = 0xffc800): void {
    if (this.list.length >= MAX) this.list.shift();
    this.list.push({ x, y: gy, z, r, t: 0, spin: R() * 6.283, zapT: 0, a, b, lobes: lobes ?? [] });
  }

  /** a lobe's centre / radius at swirl age t (mirrors CelFire's motion); false before it appears / after it ends */
  private lobeAt(l: CloudLobe, t: number, out: number[]): boolean {
    const tau = t - l.delay; if (tau < 0 || tau > l.life) return false;
    const u = tau / l.life, grow = 1 - (1 - u) ** 3;
    out[0] = l.x; out[1] = l.y + l.vy * (1 - Math.exp(-2.5 * tau)) / 2.5; out[2] = l.z; out[3] = (l.s0 + (l.s1 - l.s0) * grow) * (1 - 0.25 * u * u);
    return true;
  }

  update(dt: number, cam: THREE.Vector3): void {
    const A = [0, 0, 0, 0], B = [0, 0, 0, 0];
    for (let i = this.list.length - 1; i >= 0; i--) {
      const s = this.list[i]; s.t += dt;
      if (s.t >= LIFE) { this.list.splice(i, 1); continue; }
      const u = s.t / LIFE;
      // (owner 2026-11-02: the green/yellow swirl arms on top of the blast are removed; only the random surface electricity stays)
      // 2) random electricity over the cloud's surface
      s.zapT -= dt;
      if (s.zapT <= 0 && s.lobes.length) {
        s.zapT = 0.01 + R() * 0.035 + u * 0.04; // busy at first, thinning out as the cloud cools
        const nb = 2 + (R() < 0.7 - u * 0.4 ? 1 : 0) + (R() < 0.4 - u * 0.2 ? 1 : 0);
        for (let n = 0; n < nb; n++) {
          // a random live lobe…
          let la = -1;
          for (let tries = 0; tries < 6 && la < 0; tries++) { const k = Math.floor(R() * s.lobes.length); if (this.lobeAt(s.lobes[k], s.t, A)) la = k; }
          if (la < 0) break;
          const col = R() < 0.5 ? ZAP_G : s.b, w = (0.2 + s.r * 0.045) * (0.7 + R() * 0.6), life = 0.1 + R() * 0.14, jag = 0.3 + R() * 0.4;
          const pa = this.surfacePoint(A, cam, s.r);
          if (R() < 0.4) { // crawl over this one lobe: an arc between two facing surface points
            const pb = this.surfacePoint(A, cam, s.r), pts: number[] = [];
            for (let k = 0; k <= 4; k++) {
              const f = k / 4; let px = pa[0] + (pb[0] - pa[0]) * f - A[0], py = pa[1] + (pb[1] - pa[1]) * f - A[1], pz = pa[2] + (pb[2] - pa[2]) * f - A[2];
              const l = Math.hypot(px, py, pz) || 1, rad = A[3] * 1.08; px = px / l * rad; py = py / l * rad; pz = pz / l * rad;
              pts.push(A[0] + px, A[1] + py, A[2] + pz);
            }
            this.bolts.add(pts, col, w, life, jag * 0.7);
            continue;
          }
          // …hop to the nearest of a few other live lobes, arcing outward over the gap
          let lb = -1, best = 1e9;
          for (let tries = 0; tries < 5; tries++) {
            const k = Math.floor(R() * s.lobes.length); if (k === la || !this.lobeAt(s.lobes[k], s.t, B)) continue;
            const d = Math.hypot(B[0] - A[0], B[1] - A[1], B[2] - A[2]); if (d < best) { best = d; lb = k; }
          }
          if (lb < 0) continue;
          this.lobeAt(s.lobes[lb], s.t, B);
          const pb = this.surfacePoint(B, cam, s.r), pts: number[] = [], bulge = (A[3] + B[3]) * 0.3;
          const mx = (A[0] + B[0]) * 0.5 - s.x, my = (A[1] + B[1]) * 0.5 - s.y, mz = (A[2] + B[2]) * 0.5 - s.z, ml = Math.hypot(mx, my, mz) || 1;
          for (let k = 0; k <= 5; k++) {
            const f = k / 5, h = Math.sin(f * Math.PI) * bulge;
            pts.push(pa[0] + (pb[0] - pa[0]) * f + mx / ml * h, pa[1] + (pb[1] - pa[1]) * f + my / ml * h, pa[2] + (pb[2] - pa[2]) * f + mz / ml * h);
          }
          this.bolts.add(pts, col, w, life, jag);
        }
      }
      // a few sparks flung off the surface early on
      if (u < 0.5 && R() < 0.6) {
        const a = R() * 6.283, sp = 5 + s.r * 1.5;
        this.fx.particles.emitDir(s.x + Math.cos(a) * s.r * 0.6, s.y + 0.6 + R() * s.r, s.z + Math.sin(a) * s.r * 0.6, Math.cos(a) * sp, 1 + R() * 3, Math.sin(a) * sp, R() < 0.5 ? s.a : s.b, 0.12 + R() * 0.08, 0.25, 3);
      }
    }
    this.bolts.update(dt, cam);
  }

  /** a random point just outside lobe L (cx, cy, cz, rad), on the half that faces the camera */
  private surfacePoint(L: number[], cam: THREE.Vector3, r: number): number[] {
    let vx = cam.x - L[0], vy = cam.y - L[1], vz = cam.z - L[2]; const vl = Math.hypot(vx, vy, vz) || 1; vx /= vl; vy /= vl; vz /= vl;
    let nx = R() * 2 - 1, ny = R() * 2 - 1, nz = R() * 2 - 1;
    const dp = nx * vx + ny * vy + nz * vz; if (dp < 0) { nx -= 2 * dp * vx; ny -= 2 * dp * vy; nz -= 2 * dp * vz; } // mirror onto the facing side
    nx += vx * 0.3; ny += vy * 0.3; nz += vz * 0.3;
    const nl = Math.hypot(nx, ny, nz) || 1, rad = L[3] * 1.06 + r * 0.02;
    return [L[0] + nx / nl * rad, L[1] + ny / nl * rad, L[2] + nz / nl * rad];
  }

  clear(): void { this.list.length = 0; this.bolts.clear(); }
}
